import { Building2, Mail, Phone, MapPin, CreditCard, UserPlus } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { TenantFormData, INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS } from "@/types/tenant";

interface ReviewStepProps {
  formData: TenantFormData;
}

export function ReviewStep({ formData }: ReviewStepProps) {
  const industryLabel = INDUSTRY_OPTIONS.find(i => i.value === formData.industry)?.label || formData.industry;
  const companySizeLabel = COMPANY_SIZE_OPTIONS.find(s => s.value === formData.company_size)?.label || formData.company_size;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Review & Confirm</h2>
        <p className="text-muted-foreground">
          Please review the information before creating the tenant
        </p>
      </div>

      {/* Business Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Building2 className="h-4 w-4" />
          Business Information
        </div>
        <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-2 gap-2">
            <span className="text-sm text-muted-foreground">Company Name</span>
            <span className="text-sm font-medium">{formData.name}</span>
          </div>
          {formData.industry && (
            <div className="grid grid-cols-2 gap-2">
              <span className="text-sm text-muted-foreground">Industry</span>
              <span className="text-sm font-medium">{industryLabel}</span>
            </div>
          )}
          {formData.company_size && (
            <div className="grid grid-cols-2 gap-2">
              <span className="text-sm text-muted-foreground">Company Size</span>
              <span className="text-sm font-medium">{companySizeLabel}</span>
            </div>
          )}
          {formData.expected_device_count && (
            <div className="grid grid-cols-2 gap-2">
              <span className="text-sm text-muted-foreground">Expected Devices</span>
              <span className="text-sm font-medium">{formData.expected_device_count}</span>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Contact Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Mail className="h-4 w-4" />
          Contact Information
        </div>
        <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-2 gap-2">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm font-medium">{formData.email}</span>
          </div>
          {formData.phone && (
            <div className="grid grid-cols-2 gap-2">
              <span className="text-sm text-muted-foreground">Phone</span>
              <span className="text-sm font-medium">{formData.phone}</span>
            </div>
          )}
          {(formData.address || formData.city || formData.state) && (
            <div className="grid grid-cols-2 gap-2">
              <span className="text-sm text-muted-foreground">Address</span>
              <span className="text-sm font-medium">
                {[formData.address, formData.city, formData.state, formData.zip, formData.country]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Billing Contact */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <CreditCard className="h-4 w-4" />
          Billing Contact
        </div>
        <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
          {formData.billing_same_as_primary ? (
            <p className="text-sm">Same as primary contact ({formData.email})</p>
          ) : (
            <>
              {formData.billing_contact_name && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-sm text-muted-foreground">Name</span>
                  <span className="text-sm font-medium">{formData.billing_contact_name}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium">{formData.billing_contact_email}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Admin Invitation */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <UserPlus className="h-4 w-4" />
          Admin Invitation
        </div>
        <div className="grid gap-3 p-4 bg-muted/50 rounded-lg">
          {formData.invite_admin ? (
            <>
              {formData.admin_name && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-sm text-muted-foreground">Name</span>
                  <span className="text-sm font-medium">{formData.admin_name}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium">{formData.admin_email}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                An invitation will be sent after tenant creation
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No admin will be invited at this time</p>
          )}
        </div>
      </div>

      {/* Status Notice */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
        <p className="text-sm text-blue-700 dark:text-blue-400">
          <strong>Note:</strong> The tenant will be created with a "Creating" status. Once our team provisions the instance, the status will change to "Active".
        </p>
      </div>
    </div>
  );
}
